import { LoginOutlined } from '@ant-design/icons';
import { PageContainer, ProCard, ProTable } from '@ant-design/pro-components';
import { useMutation } from '@tanstack/react-query';
import { App, Button, Popconfirm, Result, Spin } from 'antd';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  acceptInvitation,
  queryUserInvitations,
  rejectInvitation,
} from '@/apps/auth/services/invitations';
import { useAppContext } from '@/common/hooks/AppStateProvider';

const MEMBER_ROLE = {
  owner: {
    text: 'Owner',
    status: 'Success',
  },
  admin: {
    text: 'Admin',
    status: 'Processing',
  },
  member: {
    text: 'Member',
    status: 'Default',
  },
};

const WelcomePage = () => {
  const navigate = useNavigate();
  const { session } = useAppContext();
  const { message: appMessage } = App.useApp();
  const actionRef = useRef(null);
  const [invitationState, setInvitationState] = useState({
    loading: true,
    hasPendingInvitations: false,
  });

  const refreshInvitationState = useCallback(async () => {
    try {
      const result = await queryUserInvitations();
      setInvitationState({
        loading: false,
        hasPendingInvitations: Boolean(result.data?.length),
      });
    } catch {
      setInvitationState({
        loading: false,
        hasPendingInvitations: false,
      });
    }
  }, []);

  useEffect(() => {
    if (session.user?.isPlatformAdmin) {
      navigate('/admin', { replace: true });
      return;
    }
    if (session.status !== 'authenticated') {
      return;
    }
    let ignore = false;
    const loadPendingInvitations = async () => {
      try {
        const result = await queryUserInvitations();
        if (!ignore) {
          setInvitationState({
            loading: false,
            hasPendingInvitations: Boolean(result.data?.length),
          });
        }
      } catch {
        if (!ignore) {
          setInvitationState({
            loading: false,
            hasPendingInvitations: false,
          });
        }
      }
    };
    loadPendingInvitations();
    return () => {
      ignore = true;
    };
  }, [navigate, refreshInvitationState, session.status, session.user]);

  const reloadInvitations = () => {
    actionRef.current?.reload?.();
  };

  const { mutateAsync: accept, isPending: accepting } = useMutation({
    mutationFn: acceptInvitation,
    onSuccess: (result) => {
      const organizationId = result.data?.member?.organizationId;
      appMessage.success('已加入组织');
      if (organizationId) {
        navigate(`/${encodeURIComponent(organizationId)}`, { replace: true });
        return;
      }
      reloadInvitations();
    },
    onError: () => {
      appMessage.error('接受邀请失败，请重试');
    },
  });

  const { mutateAsync: reject, isPending: rejecting } = useMutation({
    mutationFn: rejectInvitation,
    onSuccess: async () => {
      appMessage.success('已拒绝邀请');
      await refreshInvitationState();
      reloadInvitations();
    },
    onError: () => {
      appMessage.error('拒绝邀请失败，请重试');
    },
  });

  const invitationColumns = [
    {
      title: '组织名称',
      dataIndex: ['organization', 'name'],
    },
    {
      title: '角色',
      dataIndex: 'role',
      valueEnum: MEMBER_ROLE,
    },
    {
      title: '过期时间',
      dataIndex: 'expiresAt',
      valueType: 'dateTime',
      search: false,
    },
    {
      title: '操作',
      valueType: 'option',
      render: (_, record) => [
        <Button
          key="accept"
          type="link"
          loading={accepting}
          onClick={() => accept({ invitationId: record.id })}
        >
          接受
        </Button>,
        <Popconfirm
          key="reject"
          title="拒绝邀请"
          description={`确认拒绝加入 ${record.organization?.name}？`}
          okText="确认"
          cancelText="取消"
          onConfirm={() => reject({ invitationId: record.id })}
        >
          <Button type="link" danger loading={rejecting}>
            拒绝
          </Button>
        </Popconfirm>,
      ],
    },
  ];

  const handleLogout = async () => {
    try {
      await session.logout();
      appMessage.success('已退出登录');
      navigate('/', { replace: true });
    } catch (error) {
      appMessage.error('退出登录失败');
    }
  };

  const content = () => {
    if (invitationState.loading) {
      return (
        <ProCard>
          <Spin />
        </ProCard>
      );
    }
    if (invitationState.hasPendingInvitations) {
      return (
        <ProTable
          actionRef={actionRef}
          rowKey="id"
          headerTitle="待加入组织"
          request={queryUserInvitations}
          columns={invitationColumns}
          search={false}
        />
      );
    }
    return (
      <ProCard>
        <Result
          status="warning"
          title="暂无可用组织"
          subTitle="您当前不属于任何组织，请联系系统管理员为您分配组织权限。"
          extra={[
            <Button key="logout" icon={<LoginOutlined />} type="primary" onClick={handleLogout}>
              退出登录
            </Button>,
          ]}
        />
      </ProCard>
    );
  };

  return <PageContainer pageHeaderRender={false}>{content()}</PageContainer>;
};

export default WelcomePage;
